import { computed, signal } from '@angular/core';
import { FormControl } from '@angular/forms';
import {
   LcsAdvancedMultiSelectDatasource,
   LcsAdvancedMultiSelectReadonlyStateType,
   LcsAdvancedMultiSelectStateEventPayloads,
   LcsAdvancedMultiSelectTriggerConfig,
   LcsAdvancedMultiSelectTriggerFormGroup,
} from '@lcs/shared/advanced-multi-select';
import { LoadingState } from '@lcs/shared/api';
import { LcsSelectWithAllDisplayTypes } from '@lcs/shared/select-with-all';
import { ExtensionOption } from '../../extension-register/models/extension-option.interface';

export class QueueHistoryRegisterQueueFilterController {
   readonly queueFilterExtensionsControl = new FormControl<ExtensionOption[]>([], { nonNullable: true });

   readonly queueFilterAllControl = new FormControl(false, { nonNullable: true });

   readonly availableQueueExtensions = signal<ExtensionOption[]>([]);

   readonly queueFilterSearchText = signal('');

   readonly queueFilterSelectedItems = signal<ExtensionOption[]>([]);

   readonly filteredQueueExtensions = computed(() => {
      const searchText = this.queueFilterSearchText().trim().toLocaleLowerCase();
      if (!searchText.length) {
         return this.availableQueueExtensions();
      }

      return this.availableQueueExtensions().filter((queueExtension) => {
         return this.getQueueOptionLabel(queueExtension).toLocaleLowerCase().includes(searchText);
      });
   });

   readonly queueFilterDataSource = computed<LcsAdvancedMultiSelectDatasource<ExtensionOption>>(() => ({
      id: 1,
      label: '',
      items: this.filteredQueueExtensions(),
      selectedItems: this.queueFilterSelectedItems(),
      itemTrackBy: this.queueExtensionTrackBy,
      displayOptions: {
         displayWithFn: (item: ExtensionOption) => this.getQueueOptionLabel(item),
      },
      callState: LoadingState.Loaded,
   }));

   readonly queueFilterTriggerConfig = computed<LcsAdvancedMultiSelectTriggerConfig<ExtensionOption>>(() => ({
      displayOptions: {
         label: '',
         displayType: LcsSelectWithAllDisplayTypes.Label,
         displayWithFn: (items: ExtensionOption[], state: LcsAdvancedMultiSelectReadonlyStateType<ExtensionOption>) => {
            const totalQueueCount = this.availableQueueExtensions().length;
            if (!totalQueueCount) {
               return 'No Queues';
            }

            if (state.isEveryItemSelected() || items.length === totalQueueCount || items.length === undefined) {
               return 'All Queues';
            }

            if (items.length === 0) {
               return 'No Queues';
            }

            return `${items.length} Queue${items.length === 1 ? '' : 's'}`;
         },
         hasAllCheckbox: false,
         icon: 'settings',
      },
      group: new LcsAdvancedMultiSelectTriggerFormGroup({
         items: this.queueFilterExtensionsControl,
         all: this.queueFilterAllControl,
      }),
      syncOn: 'save',
   }));

   private selectedQueueExtensionIds: number[] = [];

   private isQueueFilterDialogOpen = false;

   constructor(private readonly getQueueOptionLabel: (queueExtension: ExtensionOption) => string) {}

   setQueueExtensions(queueExtensions: ExtensionOption[]): void {
      this.availableQueueExtensions.set(queueExtensions ?? []);

      if (!this.isQueueFilterDialogOpen) {
         this.syncQueueFilterControlFromSelectedIds();
      }
   }

   setSelectedQueueExtensionIds(queueExtensionIds: number[]): void {
      this.selectedQueueExtensionIds = queueExtensionIds ?? [];

      if (!this.isQueueFilterDialogOpen) {
         this.syncQueueFilterControlFromSelectedIds();
      }
   }

   onQueueFilterOpen(): void {
      this.isQueueFilterDialogOpen = true;
      this.syncQueueFilterControlFromSelectedIds();
      this.queueFilterSearchText.set('');
      this.queueFilterSelectedItems.set(this.queueFilterExtensionsControl.getRawValue() ?? []);
   }

   onQueueFilterSave(e: LcsAdvancedMultiSelectStateEventPayloads<ExtensionOption>['save']): number[] {
      const selectedQueueOptions = e.state.selectedItems();

      this.queueFilterExtensionsControl.setValue(selectedQueueOptions);
      this.queueFilterSelectedItems.set(selectedQueueOptions);

      const selectedQueueExtensionIds = selectedQueueOptions.map((queueExtension) => queueExtension.extensionId);
      this.selectedQueueExtensionIds = selectedQueueExtensionIds;

      return selectedQueueExtensionIds;
   }

   onQueueFilterSearch(e: LcsAdvancedMultiSelectStateEventPayloads<ExtensionOption>['search']): void {
      this.queueFilterSearchText.set(e.searchText ?? '');
   }

   onQueueFilterSelectedItemsChange(
      e: LcsAdvancedMultiSelectStateEventPayloads<ExtensionOption>['selectedItemsChange']
   ): void {
      this.queueFilterSelectedItems.set(e.state.selectedItems());
   }

   onQueueFilterDrop(e: LcsAdvancedMultiSelectStateEventPayloads<ExtensionOption>['drop']): void {
      this.queueFilterSelectedItems.set(e.state.selectedItems());
   }

   onQueueFilterClose(): void {
      this.isQueueFilterDialogOpen = false;
      this.queueFilterSearchText.set('');
      this.syncQueueFilterControlFromSelectedIds();
   }

   private syncQueueFilterControlFromSelectedIds(): void {
      const availableQueueExtensionsById = new Map(
         this.availableQueueExtensions().map((queueExtension) => {
            return [queueExtension.extensionId, queueExtension];
         })
      );
      const syncedQueueExtensionIds = new Set<number>();
      const selectedQueueOptions = (this.selectedQueueExtensionIds ?? []).reduce((queueOptions, queueExtensionId) => {
         if (syncedQueueExtensionIds.has(queueExtensionId)) {
            return queueOptions;
         }

         const queueExtension = availableQueueExtensionsById.get(queueExtensionId);
         if (queueExtension) {
            queueOptions.push(queueExtension);
            syncedQueueExtensionIds.add(queueExtensionId);
         }

         return queueOptions;
      }, [] as ExtensionOption[]);

      this.queueFilterExtensionsControl.setValue(selectedQueueOptions);
      this.queueFilterSelectedItems.set(selectedQueueOptions);
   }

   private readonly queueExtensionTrackBy = (queueExtension: ExtensionOption) => queueExtension.extensionId;
}
